import React, { useCallback, useMemo } from "react";
import { DepartmentType } from "../../../utils/models";
import { DEPARTMENT_TYPE_TO_COLOR } from "../../../utils/utilFunctions";
import { type SystemChipItem, SystemChips } from "../Clickable/TabsAndChips/SystemChips/SystemChips";

const DepartmentTypeColorHint = ({ type }: { type: DepartmentType }) => {
	return (
		<div
			style={{
				width: 12,
				height: 12,
				marginLeft: 6,
				background: DEPARTMENT_TYPE_TO_COLOR[type],
				borderRadius: "50%",
			}}
		/>
	);
};

export const DepartmentTypeChips = ({
	onSelectedTypeFiltersChanged,
	initiallySelected,
	minimumOneSelected = false,
}: {
	onSelectedTypeFiltersChanged: (selected: (DepartmentType | "all")[]) => void;
	initiallySelected?: (DepartmentType | "all")[];
	minimumOneSelected?: boolean;
}) => {
	const allValueForChips = useMemo<SystemChipItem<"all">>(() => ({ value: "all", label: "All" }), []);
	const typeFilterChips: SystemChipItem<DepartmentType | "all">[] = useMemo(
		() => [
			allValueForChips,
			{ value: DepartmentType.SNF, label: "SNF", startAdornment: <DepartmentTypeColorHint type={DepartmentType.SNF} /> },
			{ value: DepartmentType.ALF, label: "ALF", startAdornment: <DepartmentTypeColorHint type={DepartmentType.ALF} /> },
			{ value: DepartmentType.Hospital, label: "Hospital", startAdornment: <DepartmentTypeColorHint type={DepartmentType.Hospital} /> },
		],
		[allValueForChips],
	);
	const [selectedTypeFilters, setSelectedTypeFilters] = React.useState<SystemChipItem<DepartmentType | "all">[]>(() =>
		!initiallySelected ? [allValueForChips] : typeFilterChips.filter((value) => initiallySelected.includes(value.value)),
	);

	const handleTypeFilterChange = useCallback(
		(value: SystemChipItem<DepartmentType | "all">, selected: boolean) => {
			let updatedSelectedTypeFilters = [...selectedTypeFilters];
			if (value.value !== "all") {
				if (selected) {
					updatedSelectedTypeFilters = [...updatedSelectedTypeFilters.filter((val) => val.value !== "all"), value];
					if (updatedSelectedTypeFilters.length === typeFilterChips.length - 1) {
						updatedSelectedTypeFilters = [allValueForChips];
					}
				} else {
					if (!minimumOneSelected || updatedSelectedTypeFilters.length > 1) {
						updatedSelectedTypeFilters = updatedSelectedTypeFilters.filter((item) => item.value !== value.value);
					}
				}
			} else {
				updatedSelectedTypeFilters = [allValueForChips];
			}
			setSelectedTypeFilters(updatedSelectedTypeFilters);
			onSelectedTypeFiltersChanged(updatedSelectedTypeFilters.map((value) => value.value));
		},
		[allValueForChips, onSelectedTypeFiltersChanged, selectedTypeFilters, typeFilterChips, minimumOneSelected],
	);

	return (
		<SystemChips
			items={typeFilterChips}
			onChange={handleTypeFilterChange}
			selectedItems={selectedTypeFilters}
			forceFitContent={true}
			dataTestId="dept-chips-container"
		/>
	);
};
